import { useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ActionDescriptor } from '@finance/contracts';
import { resolveAction } from '../../../runtime/actions/action-resolution';
import {
  executeResolvedAction,
  type ActionExecutionContext,
} from '../../../runtime/actions/action-executor';
import type { ResolvedAction } from '../../../runtime/actions/action-resolution';
import { useEvaluationContext } from '../../../runtime/conditions/use-evaluation-context';
import type { TableRendererProps, TableActionContext } from './table-renderer-props.ts';
import type { UseTableDataReturn } from './use-table-data.ts';

interface UseTableActionsOptions {
  contract: TableRendererProps['contract'];
  state: UseTableDataReturn['state'];
  getRowIdValue: (row: Record<string, unknown>) => string | undefined;
  rowActivationActionProp?: ActionDescriptor;
  /** @deprecated Kept only for source compatibility; never used to synthesize actions. */
  detailPath?: string;
  onAction?: TableRendererProps['onAction'];
  onRowActivate?: (row: Record<string, unknown>, resolvedAction?: ResolvedAction) => void;
}

export interface UseTableActionsResult {
  rowActivationAction: ActionDescriptor | undefined;
  buildActionContext: (
    source: TableActionContext['source'],
    row?: Record<string, unknown>,
    selectedIds?: string[],
  ) => TableActionContext;
  handleRowActivate: (row: Record<string, unknown>, resolvedAction?: ResolvedAction) => void;
  /** Rendered actions are executed by ActionRenderer; these callbacks only dispatch business intent. */
  handleCreateAction: (resolved: ResolvedAction, context?: ActionExecutionContext) => void | Promise<void>;
  handlePageAction: (resolved: ResolvedAction, context?: ActionExecutionContext) => void | Promise<void>;
  handleBulkAction: (
    resolved: ResolvedAction,
    selectedIds: string[],
    context?: ActionExecutionContext,
  ) => void | Promise<void>;
  handleRowAction: (
    resolved: ResolvedAction,
    context: Pick<
      TableActionContext,
      'source' | 'row' | 'confirmationHandled' | 'confirmationValues'
    >,
  ) => Promise<void>;
}

export function useTableActions({
  contract,
  state,
  getRowIdValue,
  rowActivationActionProp,
  onAction,
  onRowActivate,
}: UseTableActionsOptions): UseTableActionsResult {
  const navigate = useNavigate();
  const evalCtx = useEvaluationContext();

  const rowActivationAction = useMemo<ActionDescriptor | undefined>(() => {
    if (rowActivationActionProp) return rowActivationActionProp;
    if (contract.rowActivationAction) return contract.rowActivationAction;
    return undefined;
  }, [rowActivationActionProp, contract.rowActivationAction]);

  const buildQueryFingerprint = useCallback((): string => {
    return JSON.stringify({
      sorting: state.sorting,
      filters: state.filters,
      globalSearch: state.globalSearch,
      pagination: state.pagination,
      grouping: state.grouping,
      savedViewId: state.savedViewId,
    });
  }, [state]);

  const buildActionContext = useCallback(
    (
      source: TableActionContext['source'],
      row?: Record<string, unknown>,
      selectedIds?: string[],
    ): TableActionContext => ({
      source,
      row,
      rowId: row ? getRowIdValue(row) : undefined,
      selectedIds: selectedIds ?? state.selection.selectedIds,
      tableId: String(contract.id),
      savedViewId: state.savedViewId,
      queryFingerprint: buildQueryFingerprint(),
    }),
    [getRowIdValue, state.selection.selectedIds, state.savedViewId, contract.id, buildQueryFingerprint],
  );

  const withConfirmation = useCallback(
    (tableContext: TableActionContext, actionContext?: ActionExecutionContext): TableActionContext => ({
      ...tableContext,
      confirmationHandled: actionContext?.confirmationHandled,
      confirmationValues: actionContext?.confirmationValues,
    }),
    [],
  );

  // Row activation does not render an ActionRenderer button, so it retains
  // direct execution/navigation ownership here.
  const handleRowActivate = useCallback(
    (row: Record<string, unknown>, resolvedAction?: ResolvedAction) => {
      if (onRowActivate) {
        onRowActivate(row, resolvedAction);
        return;
      }
      const context = buildActionContext('row-activation', row);
      if (resolvedAction) {
        void executeResolvedAction({
          resolved: resolvedAction,
          navigate,
          context: {
            source: 'table-row-activation',
            tableId: context.tableId,
            rowId: context.rowId,
            rowData: row,
            selectedIds: context.selectedIds,
            savedViewId: context.savedViewId,
            queryFingerprint: context.queryFingerprint,
          },
          onAction: action => onAction?.(action, context),
        });
        return;
      }
      onAction?.(undefined, context);
    },
    [onRowActivate, onAction, navigate, buildActionContext],
  );

  const handleCreateAction = useCallback(
    (resolved: ResolvedAction, actionContext?: ActionExecutionContext) => {
      const context = withConfirmation(buildActionContext('create-action'), actionContext);
      return onAction?.(resolved, context);
    },
    [buildActionContext, onAction, withConfirmation],
  );

  const handlePageAction = useCallback(
    (resolved: ResolvedAction, actionContext?: ActionExecutionContext) => {
      const context = withConfirmation(buildActionContext('page-action'), actionContext);
      return onAction?.(resolved, context);
    },
    [buildActionContext, onAction, withConfirmation],
  );

  const handleBulkAction = useCallback(
    (
      resolved: ResolvedAction,
      selectedIds: string[],
      actionContext?: ActionExecutionContext,
    ) => {
      const context = withConfirmation(
        buildActionContext('bulk-action', undefined, selectedIds),
        actionContext,
      );
      return onAction?.(resolved, context);
    },
    [buildActionContext, onAction, withConfirmation],
  );

  const handleRowAction = useCallback(
    async (
      resolved: ResolvedAction,
      context: Pick<
        TableActionContext,
        'source' | 'row' | 'confirmationHandled' | 'confirmationValues'
      >,
    ) => {
      if (!context.row && context.source !== 'empty-state') return;

      const resolvedForTarget = context.row
        ? resolveAction({ action: resolved.action, ctx: evalCtx, data: context.row })
        : resolved;
      if (!resolvedForTarget.visible || resolvedForTarget.disabled || !resolvedForTarget.available) return;

      const fullContext: TableActionContext = {
        ...buildActionContext(context.source, context.row),
        confirmationHandled: context.confirmationHandled,
        confirmationValues: context.confirmationValues,
      };
      await onAction?.(resolvedForTarget, fullContext);
    },
    [buildActionContext, evalCtx, onAction],
  );

  return {
    rowActivationAction,
    buildActionContext,
    handleRowActivate,
    handleCreateAction,
    handlePageAction,
    handleBulkAction,
    handleRowAction,
  };
}
